import type { CaptureResult, Properties } from "posthog-js";
import { enqueueEvent, drainEvents } from "@/lib/posthog/client-event-queue.mjs";
import {
  POSTHOG_FEATURE_FLAG_KEYS,
  getPostHogFeatureFlagDefaults,
  getPostHogFeatureFlagFallback,
  isPostHogFeatureFlagKey,
  type PostHogFeatureFlagKey,
} from "@/lib/feature-flags";
import { POSTHOG_EVENT_PAYLOAD_KEYS, isPostHogEventName } from "@/lib/posthog/events";
import {
  parsePostHogFeatureFlagPayload,
  type PostHogRemoteConfigPayload,
} from "@/lib/posthog/flag-payloads";
import { isPostHogCaptureDistinctId, isPostHogPortalDistinctId, withPostHogTrustedReleaseIdentity } from "@/lib/posthog/identity";
import { scrubPageNotFoundCaptureResult } from "@/lib/posthog/page-not-found-envelope";
import { getPostHogReleaseProperties } from "@/lib/posthog/release";
import {
  createPostHogConsentTransportController,
  type PostHogTransportClient,
} from "@/lib/posthog/consent-transport";
import {
  EXPERIENCE_VARIANT_REGISTRY_SOURCE_CRM_VERSION,
  isExperienceVariantRegistryExport,
  isExperienceVariantRegistryFresh,
  type ExperienceVariantRegistryExport,
} from "@/lib/experience/variant-registry";
import {
  POSTHOG_SURVEY_PROGRAM_KEYS,
  getPostHogSurveyId,
  getPostHogSurveyProgram,
  isPostHogSurveyProgramKey,
  type PostHogSurveyProgramKey,
} from "@/lib/posthog/surveys";

type ConsentState = {
  readonly analytics?: boolean;
  readonly decided?: boolean;
  readonly marketing?: boolean;
  readonly preferences?: boolean;
};

type SeohConsentApi = {
  readonly eventName?: string;
  readonly has: (category: string) => boolean;
  readonly current?: () => ConsentState;
};

type PostHogClient = PostHogTransportClient & {
  cancelPendingSurvey?: (surveyId: string) => void;
  canRenderSurveyAsync?: (surveyId: string, forceReload?: boolean) => Promise<{ readonly visible?: boolean } | null>;
  capture: (eventName: string, properties?: Properties, options?: Record<string, unknown>) => unknown;
  conversations?: {
    getCurrentTicketId?: () => string | null;
    getWidgetSessionId?: () => string | null;
    hide?: () => void;
    isAvailable?: () => boolean;
    isVisible?: () => boolean;
    loadIfEnabled?: () => void;
    show?: () => void;
  };
  displaySurvey?: (surveyId: string, options?: Record<string, unknown>) => void;
  getFeatureFlag?: (
    key: string,
    options?: { readonly send_event?: boolean },
  ) => boolean | string | undefined;
  getFeatureFlagPayload?: (key: string) => unknown;
  flush?: (transport?: "XHR" | "fetch" | "sendBeacon") => Promise<void>;
  has_opted_out_capturing?: () => boolean;
  identify?: (distinctId: string, properties?: Properties) => void;
  init: (token: string, config: Record<string, unknown>) => void;
  isFeatureEnabled?: (key: string, options?: { readonly send_event?: boolean }) => boolean | undefined;
  onFeatureFlags?: (
    callback: (
      flags?: string[],
      variants?: Record<string, boolean | string>,
      context?: { readonly errorsLoading?: boolean },
    ) => void,
  ) => () => void;
  opt_in_capturing?: (options?: Record<string, unknown>) => void;
  opt_out_capturing?: () => void;
  register?: (properties: Properties) => void;
  reloadFeatureFlags?: () => void;
  clearIdentity?: () => void;
  setIdentity?: (distinctId: string, hash: string) => void;
  setPersonProperties?: (properties?: Properties, propertiesToSetOnce?: Properties) => void;
  startSessionRecording?: (override?: Record<string, unknown>) => void;
  stopSessionRecording?: () => void;
};

type SeohDebugEvent = {
  readonly event: string;
  readonly properties: Properties;
};

type PendingMarketingEvent = {
  readonly eventName: string;
  readonly payload: Properties;
};

type BrowserExperienceVariantRegistry = ExperienceVariantRegistryExport;

type ComponentImpressionPayload = {
  readonly campaign_key?: string;
  readonly component_family: string;
  readonly component_id: string;
  readonly content_theme?: string;
  readonly cta_id?: string;
  readonly device_shell: string;
  readonly experience_id?: string;
  readonly experiment_key?: string;
  readonly experiment_variant?: string;
  readonly global_holdout?: boolean;
  readonly holdout_key?: "seoh-experience-global-holdout";
  readonly locale_direction: "ltr" | "rtl";
  readonly marketing_compliance_gate?: string;
  readonly motion_mode: string;
  readonly persona: string;
  readonly route_template_id: string;
  readonly shell_detection_state: string;
  readonly source_locale: string;
  readonly source_path: string;
  readonly taxonomy_version: string;
  readonly variant_key: string;
  readonly vertical_18plus?: boolean;
  readonly xp_depth?: number | string;
  readonly xp_zone?: string;
};

type SeohPostHogFeatureFlagSnapshot = Readonly<Record<PostHogFeatureFlagKey, boolean | string | undefined>>;
type SeohPostHogSupportIdentityPayload = {
  readonly distinct_id?: string;
  readonly hash?: string;
  readonly identity_distinct_id?: string;
  readonly identity_hash?: string;
};

declare global {
  interface Navigator {
    standalone?: boolean;
  }

  interface Window {
    __seohConsent?: SeohConsentApi;
    __seohPostHogCapturePageview?: () => boolean;
    __seohPostHogDebugEvents?: SeohDebugEvent[];
    __seohPostHogFeatureFlagDefaults?: Readonly<Record<PostHogFeatureFlagKey, boolean>>;
    __seohPostHogCancelSurvey?: (programKey: string) => boolean;
    __seohPostHogCanRenderSurvey?: (programKey: string, forceReload?: boolean) => Promise<boolean>;
    __seohPostHogDisplaySurvey?: (programKey: string, options?: Record<string, unknown>) => Promise<boolean>;
    __seohPostHogGetFeatureFlag?: (key: string) => boolean | string | undefined;
    __seohPostHogGetFeatureFlagPayload?: (key: string) => PostHogRemoteConfigPayload | undefined;
    __seohPostHogGetFeatureFlags?: () => SeohPostHogFeatureFlagSnapshot;
    __seohPostHogGetRemoteConfigPayload?: (key: string) => PostHogRemoteConfigPayload | undefined;
    __seohPostHogGetExperiencePreviewVariant?: () => string | undefined;
    __seohPostHogClearPortalIdentity?: () => boolean;
    __seohPostHogIdentifyPortalUser?: (safeUserId?: string | null) => boolean;
    __seohPostHogInstrumentationMounted?: boolean;
    __seohPostHogIsFeatureEnabled?: (key: string) => boolean;
    __seohPostHogHideSupportWidget?: () => boolean;
    __seohPostHogResyncComponentImpressions?: () => void;
    __seohPostHogClearSupportIdentity?: () => boolean;
    __seohPostHogQaMode?: () => string | undefined;
    __seohPostHogReloadFeatureFlags?: () => boolean;
    __seohPostHogSetQaMode?: (value?: string | null) => string | undefined;
    __seohPostHogSetSupportIdentity?: (payload?: SeohPostHogSupportIdentityPayload | null) => boolean;
    __seohPostHogShowSupportWidget?: () => boolean;
    __seohPostHogSubscribeToFeatureFlags?: (
      callback: (snapshot: SeohPostHogFeatureFlagSnapshot) => void,
    ) => (() => void) | undefined;
    __seohPostHogSupportTicketId?: () => string | null;
    __seohPostHogSupportWidgetAvailable?: () => boolean;
    __seohPostHogSupportWidgetSessionId?: () => string | null;
    __seohPostHogSupportWidgetVisible?: () => boolean;
    __seohPostHogSurveyProgramKeys?: readonly PostHogSurveyProgramKey[];
    __seohPostHogCaptureComponentEngagement?: (payload: Record<string, unknown>) => boolean;
    __seohPostHogTrackEvent?: (eventName: string, payload?: Record<string, unknown>) => boolean;
    __seohPostHogGetDeliveryDiagnostics?: () => ReturnType<typeof getMarketingDeliveryDiagnostics>;
    __seohPostHogGetLocalSharedProperties?: () => Properties;
    __seohPostHogFlush?: () => Promise<void>;
  }
}

const POSTHOG_ENABLED = process.env.NEXT_PUBLIC_POSTHOG_ENABLED === "true";
const POSTHOG_TOKEN =
  process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN || process.env.NEXT_PUBLIC_POSTHOG_TOKEN || "";
const CONFIGURED_POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || "";
const POSTHOG_HOST =
  CONFIGURED_POSTHOG_HOST && CONFIGURED_POSTHOG_HOST !== "https://eu.i.posthog.com"
    ? CONFIGURED_POSTHOG_HOST
    : "https://t.seoh.team";
const POSTHOG_UI_HOST = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST || "https://eu.posthog.com";
const POSTHOG_REPLAY_ENABLED = process.env.NEXT_PUBLIC_POSTHOG_REPLAY_ENABLED === "true";
const POSTHOG_REPLAY_BLOCKED_ROUTE_PREFIXES = [
  "/admin",
  "/api",
  "/auth",
  "/client-mcp",
  "/client-portal",
  "/contact",
  "/crm",
  "/login",
  "/not-found-telemetry",
  "/support",
] as const;
const POSTHOG_TOOLBAR_QUERY_PARAM = process.env.NEXT_PUBLIC_POSTHOG_TOOLBAR_PARAM || "seoh_toolbar";
const POSTHOG_TOOLBAR_STORAGE_KEY = "_postHogToolbarParams";
const POSTHOG_TOOLBAR_ELEMENT_ID = "__POSTHOG_TOOLBAR__";

const APP_LOCALES = [
  "en",
  "ru",
  "ar",
  "es",
  "fr",
  "he",
  "de",
  "pt-br",
  "ja",
  "hi",
  "id",
  "zh-cn",
  "tr",
  "ko",
  "it",
] as const;

const LOCALE_PATTERN = new RegExp(`^/(${APP_LOCALES.join("|")})(?=/|$)`, "i");
const MARKETING_PAYLOAD_KEYS = POSTHOG_EVENT_PAYLOAD_KEYS;
const REDACTED_QUERY_PARAMS = new Set([
  "token",
  "api_key",
  "code",
  "email",
  "auth",
  "access_token",
  "refresh_token",
  "magic_link",
  "otp",
]);
const SENSITIVE_PROPERTY_KEY_PATTERN =
  /email|phone|company|message|body|token|api_key|auth|access_token|refresh_token|magic_link|supabase|(^|_)code($|_)/i;
const PII_VALUE_PATTERN = /@|(?:\+?\d[\d\s().-]{5,}\d)/;
const MARKETING_VALUE_MAX_LENGTH = 120;
const PROPERTY_VALUE_MAX_LENGTH = 500;
const POSTHOG_CAPTURE_OPTIONS = { send_instantly: true };
const POSTHOG_FEATURE_FLAG_CHECK_OPTIONS = { send_event: false } as const;
const POSTHOG_FEATURE_FLAG_DEFAULTS = getPostHogFeatureFlagDefaults();
const POSTHOG_SUPPORT_IDENTITY_HASH_PATTERN = /^[a-f0-9]{64}$/i;
const POSTHOG_SUPPORT_IDENTITY_ID_PATTERN = /^[a-zA-Z0-9:_-]{8,160}$/;
const POSTHOG_RELEASE_PROPERTIES = getPostHogReleaseProperties({
  NEXT_PUBLIC_GIT_BRANCH: process.env.NEXT_PUBLIC_GIT_BRANCH,
  NEXT_PUBLIC_GIT_SHA: process.env.NEXT_PUBLIC_GIT_SHA,
  NEXT_PUBLIC_SEOH_DEPLOY_ENV: process.env.NEXT_PUBLIC_SEOH_DEPLOY_ENV,
  NEXT_PUBLIC_SEOH_DEPLOYMENT_ID: process.env.NEXT_PUBLIC_SEOH_DEPLOYMENT_ID,
  NEXT_PUBLIC_SEOH_RELEASE_ID: process.env.NEXT_PUBLIC_SEOH_RELEASE_ID,
  NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID: process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID,
  NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF,
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
});
const POSTHOG_WEB_VITALS = ["CLS", "FCP", "LCP", "INP"] as const;
const POSTHOG_INTERNAL_TOKEN_KEYS = new Set(["token", "api_key"]);
const POSTHOG_UNSCRUBBED_INTERNAL_EVENTS = new Set(["$snapshot"]);
const POSTHOG_PROPERTY_DENYLIST = ["access_token", "refresh_token", "magic_link", "otp"];
const POSTHOG_PERSONAL_DATA_PROPERTIES = Array.from(REDACTED_QUERY_PARAMS).filter(
  (propertyName) => !POSTHOG_INTERNAL_TOKEN_KEYS.has(propertyName),
);
const EXPERIENCE_VARIANT_REGISTRY_SCRIPT_ID = "seoh-experience-variant-registry";
const EXPERIENCE_VARIANT_REGISTRY_URL = "/api/experience-variant-registry";
const EXPERIENCE_VARIANT_REGISTRY_TIMEOUT_MS = 3_000;
const COMPONENT_IMPRESSION_SELECTOR =
  "[data-analytics-component-id], [data-analytics-cta-id], [data-analytics-experience-id], [data-seoh-xp-zone]";
const COMPONENT_IMPRESSION_TRACKING_TAXONOMY_VERSION = "component-impression-threshold-v1";
const COMPONENT_IMPRESSION_THRESHOLD = 0.5;
const COMPONENT_IMPRESSION_DWELL_MS = 600;
const COMPONENT_IMPRESSION_SAFE_TOKEN_PATTERN = /^[a-z0-9][a-z0-9_.:/()[\]-]{0,159}$/i;
const COMPONENT_ENGAGEMENT_ACTION_SELECTOR = [
  "a[href]",
  "button",
  "input[type='button']",
  "input[type='submit']",
  "input[type='reset']",
  "summary",
  "[role='button']",
  "[role='link']",
  "[data-analytics-event]",
  "[data-analytics-cta-id]",
  "[data-fit-quiz-submit]",
  "[data-fit-quiz-next]",
  "[data-package-audience]",
  "[data-package-service]",
  "[data-package-addon]",
  "[data-service-filter-value]",
  "[data-portfolio-filter-value]",
  "[data-roi-book-cta]",
].join(",");
const COMPONENT_ENGAGEMENT_KEY_SELECTOR = [
  "a[href]",
  "button",
  "input[type='button']",
  "input[type='submit']",
  "input[type='reset']",
  "summary",
  "[role='button']",
  "[role='link']",
  "[data-analytics-cta-id]",
].join(",");
const EXPERIENCE_ZONES = new Set(["card", "chrome", "cta", "footer", "form", "hero", "media", "panel", "progress", "reading", "section", "static"]);
const QA_MODE_STORAGE_KEY = "seoh:analytics-qa-mode";
const SYNTHETIC_QA_RUN_ID_STORAGE_KEY = "seoh:posthog-synthetic-qa-run-id";
const SYNTHETIC_QA_RUN_ID_PATTERN = /^qa_[a-z0-9][a-z0-9_-]{0,59}$/;
const QA_MODE_CLEAR_VALUES = new Set(["0", "false", "off", "clear", "disable", "disabled", "none"]);
const EXPERIENCE_PREVIEW_QA_MODE = "experience-preview";
const EXPERIENCE_PREVIEW_VARIANT_KEYS = new Set(["proof_first", "component_proof_density"]);
const HOLDOUT_VARIANT_PREFIX = "holdout-";
const EXPERIENCE_GLOBAL_HOLDOUT_KEY = "seoh-experience-global-holdout";
const AGENT_OR_CRAWLER_USER_AGENT_PATTERN =
  /(?:bot|crawler|spider|slurp|bingpreview|google-inspectiontool|chatgpt|gptbot|oai-searchbot|claude|anthropic|perplexity|copilot|gemini|bytespider|ccbot|facebookexternalhit|linkedinbot|whatsapp|telegrambot)/i;
const SESSION_ENTRY_DIMENSIONS_KEY = "seoh:analytics-entry-dimensions";
const SESSION_ACQUISITION_DIMENSIONS_KEY = "seoh:analytics-acquisition-dimensions";
const SESSION_PAGE_DEPTH_KEY = "seoh:analytics-page-depth";
const SESSION_VISIT_PROFILE_KEY = "seoh:analytics-visit-profile";
const VISIT_NUMBER_STORAGE_KEY = "seoh:analytics-visit-number";
const LAST_VISIT_AT_STORAGE_KEY = "seoh:analytics-last-visit-at";
const ORIGINAL_REFERRER_STORAGE_KEY = "seoh:analytics-original-referrer";
const SEARCH_REFERRER_DOMAINS = [
  "google.",
  "bing.com",
  "duckduckgo.com",
  "search.yahoo.",
  "yandex.",
  "baidu.",
  "ecosia.org",
  "search.brave.com",
];
const SOCIAL_REFERRER_DOMAINS = [
  "facebook.com",
  "instagram.com",
  "linkedin.com",
  "lnkd.in",
  "t.co",
  "twitter.com",
  "x.com",
  "youtube.com",
  "youtu.be",
  "reddit.com",
  "pinterest.",
  "tiktok.com",
];
const AI_REFERRER_DOMAINS = [
  "chatgpt.com",
  "claude.ai",
  "perplexity.ai",
  "copilot.microsoft.com",
  "gemini.google.com",
  "you.com",
];
const PAID_SEARCH_MEDIUMS = new Set(["cpc", "ppc", "paidsearch", "paid-search", "sem"]);

let posthogClient: PostHogClient | null = null;
let posthogLoadPromise: Promise<PostHogClient | null> | null = null;
let pendingMarketingEvents: PendingMarketingEvent[] = [];
type MarketingDeliveryState = "constructed" | "queued" | "handed_to_collector" | "delivery_unknown" | "discarded" | "local_debug";
const marketingDeliveryDiagnostics = {
  queued: 0,
  droppedOverflow: 0,
  droppedOnWithdrawal: 0,
  handedToCollector: 0,
  deliveryUnknown: 0,
  lastState: "constructed" as MarketingDeliveryState,
};
const getMarketingDeliveryDiagnostics = () => ({
  ...marketingDeliveryDiagnostics,
  queueDepth: pendingMarketingEvents.length,
  // The SDK handoff has no collector acknowledgement callback.
  collectorAccepted: null,
});
let appOpenedCaptured = false;
let lastPageviewKey = "";
let lastRouterTransitionTimer: number | null = null;
let componentImpressionObserver: IntersectionObserver | null = null;
let componentImpressionMutationObserver: MutationObserver | null = null;
let componentImpressionObservedElements = new WeakSet<Element>();
let componentImpressionVisibleElements = new WeakSet<Element>();
let componentImpressionVisibleTimers = new WeakMap<Element, number>();
let componentImpressionSeenKeys = new Set<string>();
let componentEngagementSeenKeys = new Set<string>();
let componentImpressionRouteKey = "";
let componentImpressionSyncFrameRequested = false;
let browserExperienceRegistryCache: BrowserExperienceVariantRegistry | null | undefined;
let browserExperienceRegistryLoadPromise: Promise<BrowserExperienceVariantRegistry | null> | undefined;
let browserExperienceRegistryReady = false;

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";
const isConfigured = () => POSTHOG_ENABLED && POSTHOG_TOKEN.trim().length > 0;
const isPageNotFoundTelemetrySurface = () =>
  isBrowser() && /^\/not-found-telemetry\/[a-z]{2}(?:-[a-z]{2})?\/?$/i.test(window.location.pathname);

const hasExplicitPostHogToolbarOptIn = () => {
  if (!isBrowser()) {
    return false;
  }

  try {
    const params = new URLSearchParams(window.location.search);

    if (!params.has(POSTHOG_TOOLBAR_QUERY_PARAM)) {
      return false;
    }

    const value = (params.get(POSTHOG_TOOLBAR_QUERY_PARAM) || "").trim().toLowerCase();

    return value === "" || value === "1" || value === "true" || value === "yes" || value === "on";
  } catch {
    return false;
  }
};

const isPostHogToolbarHash = (hash: string) => {
  if (!hash) {
    return false;
  }

  try {
    const decodedHash = decodeURIComponent(hash);

    return (
      decodedHash.includes("__posthog") ||
      decodedHash.includes("__posthog_toolbar") ||
      decodedHash.includes("ph_authorize")
    );
  } catch {
    return hash.includes("__posthog") || hash.includes("__posthog_toolbar") || hash.includes("ph_authorize");
  }
};

const disablePostHogToolbarUnlessAllowed = () => {
  if (!isBrowser() || hasExplicitPostHogToolbarOptIn()) {
    return;
  }

  try {
    window.localStorage.removeItem(POSTHOG_TOOLBAR_STORAGE_KEY);
  } catch {
    // Toolbar state cleanup is best-effort when storage is unavailable.
  }

  if (isPostHogToolbarHash(window.location.hash)) {
    try {
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
    } catch {
      window.location.hash = "";
    }
  }

  document.getElementById(POSTHOG_TOOLBAR_ELEMENT_ID)?.remove();
};

const getConsentApi = () => {
  if (!isBrowser()) {
    return null;
  }

  const api = window.__seohConsent;
  return api && typeof api.has === "function" ? api : null;
};

const hasAnalyticsConsent = () => getConsentApi()?.has("analytics") === true;

const redactUrl = (value: string) => {
  try {
    const isRelativePath = value.startsWith("/") && !value.startsWith("//");
    const url = new URL(value, window.location.origin);

    if (url.protocol === "mailto:" || url.protocol === "tel:") {
      return url.protocol.slice(0, -1);
    }

    url.search = "";

    if (isRelativePath) {
      return `${url.pathname}${url.hash}`;
    }

    if (url.protocol === "http:" || url.protocol === "https:") {
      return `${url.origin}${url.pathname}${url.hash}`;
    }

    return url.toString();
  } catch {
    return value;
  }
};

const redactUrlLikeValue = (key: string, value: string) => {
  const lowerKey = key.toLowerCase();
  const trimmed = value.trim().slice(0, PROPERTY_VALUE_MAX_LENGTH);

  if (!trimmed) {
    return "";
  }

  if (lowerKey.includes("url") || trimmed.startsWith("/") || trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return redactUrl(trimmed);
  }

  return trimmed;
};

const scrubStringProperty = (key: string, value: string) => {
  const redacted = redactUrlLikeValue(key, value);

  if (!redacted) {
    return undefined;
  }

  if (!key.startsWith("$") && PII_VALUE_PATTERN.test(redacted)) {
    return undefined;
  }

  return redacted;
};

const isSensitiveCustomPropertyKey = (key: string) => !key.startsWith("$") && SENSITIVE_PROPERTY_KEY_PATTERN.test(key);

type ScrubOptions = {
  readonly preservePostHogInternalToken?: boolean;
};

const isPostHogInternalTokenProperty = (key: string, value: unknown) =>
  POSTHOG_INTERNAL_TOKEN_KEYS.has(key) && typeof value === "string" && value === POSTHOG_TOKEN;

const scrubPropertyValue = (key: string, value: unknown, depth = 0, options: ScrubOptions = {}): unknown => {
  if (options.preservePostHogInternalToken && isPostHogInternalTokenProperty(key, value)) {
    return value;
  }

  if (isSensitiveCustomPropertyKey(key)) {
    return undefined;
  }

  if (typeof value === "string") {
    return scrubStringProperty(key, value);
  }

  if (typeof value === "number" || typeof value === "boolean" || value === null) {
    return value;
  }

  if (depth > 5) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const scrubbedArray = value
      .map((item) => scrubPropertyValue(key, item, depth + 1, options))
      .filter((item) => item !== undefined);

    return scrubbedArray.length > 0 || key.startsWith("$") ? scrubbedArray : undefined;
  }

  if (typeof value === "object" && value) {
    const safeObject: Record<string, unknown> = {};

    Object.entries(value as Record<string, unknown>).forEach(([childKey, childValue]) => {
      const scrubbed = scrubPropertyValue(childKey, childValue, depth + 1, options);

      if (scrubbed !== undefined) {
        safeObject[childKey] = scrubbed;
      }
    });

    return Object.keys(safeObject).length > 0 || key.startsWith("$") ? safeObject : undefined;
  }

  return undefined;
};

const scrubProperties = (properties: Properties | Record<string, unknown> = {}, options: ScrubOptions = {}) => {
  const safeProperties: Properties = {};

  Object.entries(properties).forEach(([key, value]) => {
    // Only the SDK-merged event envelope may retain an opaque identity. Custom
    // properties and nested/person properties still use the normal PII filter.
    if (options.preservePostHogInternalToken && key === "distinct_id") {
      if (isPostHogCaptureDistinctId(value)) safeProperties[key] = value;
      return;
    }
    const scrubbed = scrubPropertyValue(key, value, 0, options);

    if (scrubbed !== undefined) {
      (safeProperties as Record<string, unknown>)[key] = scrubbed;
    }
  });

  return safeProperties;
};

const isLocalDebugHost = () => window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost";

const recordLocalDebugEvent = (eventName: string, properties: Properties) => {
  if (!isLocalDebugHost()) {
    return;
  }

  window.__seohPostHogDebugEvents = window.__seohPostHogDebugEvents || [];
  window.__seohPostHogDebugEvents.push({
    event: eventName,
    properties,
  });
};

const applySyntheticQaCaptureProperties = (captureResult: CaptureResult): CaptureResult => {
  const qaRunId = preparedSyntheticQaRunId || getSyntheticQaRunId();

  if (!qaRunId) {
    return captureResult;
  }

  return {
    ...captureResult,
    properties: {
      ...captureResult.properties,
      qa_run_id: qaRunId,
    },
    $set: {
      ...(captureResult.$set || {}),
      $internal_or_test_user: true,
    },
  };
};

const scrubCaptureResult = (captureResult: CaptureResult | null) => {
  if (!captureResult || !canCapturePostHog()) {
    return null;
  }

  const syntheticQaCapture = applySyntheticQaCaptureProperties(captureResult);

  if (POSTHOG_UNSCRUBBED_INTERNAL_EVENTS.has(String(syntheticQaCapture.event))) {
    return syntheticQaCapture;
  }

  if (syntheticQaCapture.event === "page_not_found") {
    const scrubbedNotFoundCapture = scrubPageNotFoundCaptureResult(
      syntheticQaCapture,
      POSTHOG_TOKEN,
      POSTHOG_RELEASE_PROPERTIES,
    );
    const taggedNotFoundCapture = scrubbedNotFoundCapture
      ? applySyntheticQaCaptureProperties(scrubbedNotFoundCapture)
      : null;

    if (taggedNotFoundCapture && isLocalDebugHost()) {
      recordLocalDebugEvent(String(taggedNotFoundCapture.event), taggedNotFoundCapture.properties);
    }

    return taggedNotFoundCapture;
  }

  const scrubbed = {
    ...syntheticQaCapture,
    properties: withPostHogTrustedReleaseIdentity(
      scrubProperties(syntheticQaCapture.properties, { preservePostHogInternalToken: true }),
      POSTHOG_RELEASE_PROPERTIES,
    ),
    $set: syntheticQaCapture.$set ? scrubProperties(syntheticQaCapture.$set) : syntheticQaCapture.$set,
    $set_once: syntheticQaCapture.$set_once
      ? scrubProperties(syntheticQaCapture.$set_once)
      : syntheticQaCapture.$set_once,
  };
  const taggedScrubbed = applySyntheticQaCaptureProperties(scrubbed);

  if (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost") {
    recordLocalDebugEvent(String(taggedScrubbed.event), taggedScrubbed.properties);
  }

  return taggedScrubbed;
};

const stripLocale = (pathname: string) => pathname.replace(LOCALE_PATTERN, "") || "/";

const isPostHogReplayRouteAllowed = () => {
  if (!isBrowser()) {
    return false;
  }

  const routePath = stripLocale(window.location.pathname).toLowerCase();
  return !POSTHOG_REPLAY_BLOCKED_ROUTE_PREFIXES.some(
    (prefix) => routePath === prefix || routePath.startsWith(`${prefix}/`),
  );
};

const canRecordPostHogReplay = () =>
  POSTHOG_REPLAY_ENABLED &&
  isPostHogReplayRouteAllowed() &&
  !isPageNotFoundTelemetrySurface() &&
  canCapturePostHog();

const syncPostHogReplayState = (client: PostHogClient | null = posthogClient) => {
  if (!client) {
    return false;
  }

  if (!canRecordPostHogReplay()) {
    client.stopSessionRecording?.();
    return false;
  }

  client.startSessionRecording?.();
  return true;
};

const normalizeDimensionValue = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9#/_:.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, MARKETING_VALUE_MAX_LENGTH);

const safeSearchParam = (name: string) => {
  try {
    const value = new URLSearchParams(window.location.search).get(name)?.trim();

    if (!value || PII_VALUE_PATTERN.test(value) || /(?:https?:\/\/|www\.)/i.test(value)) {
      return undefined;
    }

    return normalizeDimensionValue(value) || undefined;
  } catch {
    return undefined;
  }
};

const setStoredQaMode = (value?: string | null) => {
  if (!isBrowser()) {
    return undefined;
  }

  const normalizedValue = value ? normalizeDimensionValue(value) : "";

  try {
    if (!normalizedValue || QA_MODE_CLEAR_VALUES.has(normalizedValue)) {
      window.localStorage.removeItem(QA_MODE_STORAGE_KEY);
      return undefined;
    }

    window.localStorage.setItem(QA_MODE_STORAGE_KEY, normalizedValue);
    return normalizedValue;
  } catch {
    return normalizedValue || undefined;
  }
};

const getStoredQaMode = () => {
  if (!isBrowser()) {
    return undefined;
  }

  const queryMode = safeSearchParam("qa");

  if (queryMode) {
    return setStoredQaMode(queryMode);
  }

  try {
    const storedMode = window.localStorage.getItem(QA_MODE_STORAGE_KEY);
    const normalizedMode = storedMode ? normalizeDimensionValue(storedMode) : "";

    return normalizedMode && !QA_MODE_CLEAR_VALUES.has(normalizedMode) ? normalizedMode : undefined;
  } catch {
    return undefined;
  }
};

const isQaAnalyticsSuppressed = () => Boolean(getStoredQaMode());

let preparedSyntheticQaRunId: string | undefined;

const getSyntheticQaRunId = () => {
  if (!isBrowser()) {
    return undefined;
  }

  try {
    const storedValue = window.sessionStorage.getItem(SYNTHETIC_QA_RUN_ID_STORAGE_KEY)?.trim() || "";

    return SYNTHETIC_QA_RUN_ID_PATTERN.test(storedValue) ? storedValue : undefined;
  } catch {
    return undefined;
  }
};

const getSyntheticQaProperties = (): Properties => {
  const qaRunId = preparedSyntheticQaRunId || getSyntheticQaRunId();

  return qaRunId ? { qa_run_id: qaRunId } : {};
};

const markSyntheticQaPerson = (client: PostHogClient, qaRunId = getSyntheticQaRunId()) => {
  if (!qaRunId) {
    return;
  }

  client.setPersonProperties?.({ $internal_or_test_user: true });
  recordLocalDebugEvent("$set", { qa_run_id: qaRunId, $internal_or_test_user: true });
};

const preparePostHogCapture = (client: PostHogClient, telemetryOnly: boolean) => {
  const qaRunId = getSyntheticQaRunId();
  preparedSyntheticQaRunId = qaRunId;

  if (qaRunId) {
    // Register before any SDK capture and suppress the opt-in event for this isolated QA session.
    client.register?.(getSharedProperties());
    client.opt_in_capturing?.({ captureEventName: false });
    markSyntheticQaPerson(client, qaRunId);
    return;
  }

  client.opt_in_capturing?.({ captureEventName: telemetryOnly ? false : "$opt_in" });
  client.register?.(getSharedProperties());
};

const isAgentOrCrawlerVisitor = () => {
  if (!isBrowser()) {
    return false;
  }

  const explicitCrawler = document.documentElement.getAttribute("data-search-crawler");
  if (explicitCrawler && explicitCrawler !== "none") {
    return true;
  }

  return AGENT_OR_CRAWLER_USER_AGENT_PATTERN.test(navigator.userAgent || "");
};

const canCapturePostHog = () => hasAnalyticsConsent() && !isQaAnalyticsSuppressed() && !isAgentOrCrawlerVisitor();

const posthogConsentTransport = createPostHogConsentTransportController(canCapturePostHog);

const getForcedExperiencePreviewVariantKey = () => {
  if (getStoredQaMode() !== EXPERIENCE_PREVIEW_QA_MODE) {
    return undefined;
  }

  const candidate = normalizeExperienceToken(safeSearchParam("seoh_xp_variant"), "");
  return EXPERIENCE_PREVIEW_VARIANT_KEYS.has(candidate) ? candidate : undefined;
};

const isHoldoutVariantKey = (value: unknown) => typeof value === "string" && value.startsWith(HOLDOUT_VARIANT_PREFIX);

const syncExperiencePreviewMetadata = () => {
  const previewVariantKey = getForcedExperiencePreviewVariantKey();

  if (previewVariantKey) {
    document.documentElement.setAttribute("data-seoh-xp-preview-variant", previewVariantKey);
    return;
  }

  document.documentElement.removeAttribute("data-seoh-xp-preview-variant");
};

const getPostHogFeatureFlagValue = (key: string) => {
  if (!isPostHogFeatureFlagKey(key)) {
    return undefined;
  }

  const fallback = getPostHogFeatureFlagFallback(key);

  if (!posthogClient || !canCapturePostHog()) {
    return fallback;
  }

  return posthogClient.getFeatureFlag?.(key, POSTHOG_FEATURE_FLAG_CHECK_OPTIONS) ?? fallback;
};

const isPostHogFeatureEnabled = (key: string) => {
  if (!isPostHogFeatureFlagKey(key)) {
    return false;
  }

  const fallback = getPostHogFeatureFlagFallback(key);

  if (!posthogClient || !canCapturePostHog()) {
    return fallback;
  }

  return posthogClient.isFeatureEnabled?.(key, POSTHOG_FEATURE_FLAG_CHECK_OPTIONS) ?? fallback;
};

const getPostHogFeatureFlagPayload = (key: string) => {
  if (!isPostHogFeatureFlagKey(key) || !isPostHogFeatureEnabled(key) || !posthogClient || !canCapturePostHog()) {
    return undefined;
  }

  return parsePostHogFeatureFlagPayload(key, posthogClient.getFeatureFlagPayload?.(key));
};

const getPostHogFeatureFlagSnapshot = (): SeohPostHogFeatureFlagSnapshot =>
  Object.fromEntries(POSTHOG_FEATURE_FLAG_KEYS.map((key) => [key, getPostHogFeatureFlagValue(key)])) as SeohPostHogFeatureFlagSnapshot;

const reloadPostHogFeatureFlags = () => {
  if (!posthogClient || !canCapturePostHog()) {
    return false;
  }

  posthogClient.reloadFeatureFlags?.();
  return true;
};

const subscribeToPostHogFeatureFlags = (callback: (snapshot: SeohPostHogFeatureFlagSnapshot) => void) => {
  callback(getPostHogFeatureFlagSnapshot());

  if (!posthogClient || !canCapturePostHog() || !posthogClient.onFeatureFlags) {
    return undefined;
  }

  return posthogClient.onFeatureFlags(() => callback(getPostHogFeatureFlagSnapshot()));
};

const isSupportWidgetFlagEnabled = () => isPostHogFeatureEnabled("seoh-support-widget");

const isPostHogSupportWidgetAvailable = () =>
  Boolean(posthogClient?.conversations?.isAvailable?.() && canCapturePostHog() && isSupportWidgetFlagEnabled());

const showPostHogSupportWidget = () => {
  if (!posthogClient || !canCapturePostHog() || !isSupportWidgetFlagEnabled()) {
    return false;
  }

  posthogClient.conversations?.loadIfEnabled?.();
  posthogClient.conversations?.show?.();
  return posthogClient.conversations?.isVisible?.() === true;
};

const hidePostHogSupportWidget = () => {
  if (!posthogClient?.conversations || !canCapturePostHog()) {
    return false;
  }

  posthogClient.conversations.hide?.();
  return true;
};

const getPostHogSupportTicketId = () => {
  if (!posthogClient?.conversations || !canCapturePostHog()) {
    return null;
  }

  return posthogClient.conversations.getCurrentTicketId?.() || null;
};

const getPostHogSupportWidgetSessionId = () => {
  if (!posthogClient?.conversations || !canCapturePostHog()) {
    return null;
  }

  return posthogClient.conversations.getWidgetSessionId?.() || null;
};

const setPostHogSupportIdentity = (payload?: SeohPostHogSupportIdentityPayload | null) => {
  const distinctId = String(payload?.identity_distinct_id || payload?.distinct_id || "").trim();
  const hash = String(payload?.identity_hash || payload?.hash || "").trim();

  if (
    !posthogClient ||
    !canCapturePostHog() ||
    !POSTHOG_SUPPORT_IDENTITY_ID_PATTERN.test(distinctId) ||
    !POSTHOG_SUPPORT_IDENTITY_HASH_PATTERN.test(hash)
  ) {
    return false;
  }

  posthogClient.setIdentity?.(distinctId, hash);
  return true;
};

const clearPostHogSupportIdentity = () => {
  if (!posthogClient || !canCapturePostHog()) {
    return false;
  }

  posthogClient.clearIdentity?.();
  return true;
};

const getSurveyIdForProgram = (programKey: string) => {
  if (!isPostHogSurveyProgramKey(programKey)) {
    return undefined;
  }

  return getPostHogSurveyId(programKey);
};

const isSurveyTargetFlagEnabled = (programKey: PostHogSurveyProgramKey) => {
  const surveyProgram = getPostHogSurveyProgram(programKey);
  const targetFlagKey = surveyProgram && "targetFlagKey" in surveyProgram ? surveyProgram.targetFlagKey : undefined;

  return !targetFlagKey || (isPostHogFeatureFlagKey(targetFlagKey) && isPostHogFeatureEnabled(targetFlagKey));
};

const canRenderPostHogSurvey = async (programKey: string, forceReload = false) => {
  if (!isPostHogSurveyProgramKey(programKey) || !isSurveyTargetFlagEnabled(programKey)) {
    return false;
  }

  const surveyId = getSurveyIdForProgram(programKey);

  if (!surveyId || !posthogClient || !canCapturePostHog() || !posthogClient.canRenderSurveyAsync) {
    return false;
  }

  try {
    const renderReason = await posthogClient.canRenderSurveyAsync(surveyId, forceReload);

    return renderReason?.visible === true;
  } catch {
    return false;
  }
};

const displayPostHogSurvey = async (programKey: string, options: Record<string, unknown> = {}) => {
  if (!isPostHogSurveyProgramKey(programKey) || !(await canRenderPostHogSurvey(programKey))) {
    return false;
  }

  const surveyId = getSurveyIdForProgram(programKey);

  if (!surveyId || !posthogClient?.displaySurvey) {
    return false;
  }

  posthogClient.displaySurvey(surveyId, options);
  return true;
};

const cancelPostHogSurvey = (programKey: string) => {
  const surveyId = getSurveyIdForProgram(programKey);

  if (!surveyId || !posthogClient?.cancelPendingSurvey) {
    return false;
  }

  posthogClient.cancelPendingSurvey(surveyId);
  return true;
};

const getSiteSection = (routePath: string) => {
  const [section = ""] = routePath.split("/").filter(Boolean);

  return section ? normalizeDimensionValue(section) : "home";
};

const getPageType = (routePath: string) => {
  if (routePath === "/") return "home";
  if (routePath === "/services" || routePath.startsWith("/services/")) return "service";
  if (routePath === "/resources" || routePath.startsWith("/resources/")) return "resource";
  if (routePath.startsWith("/blog")) return "blog";
  if (routePath.startsWith("/client-portal")) return "client_portal";
  if (routePath.startsWith("/contact")) return "contact";
  if (routePath.startsWith("/support")) return "support";
  if (routePath.startsWith("/cancellation")) return "cancellation";
  if (routePath.startsWith("/privacy") || routePath.startsWith("/terms") || routePath.startsWith("/legal")) return "legal";
  if (routePath.startsWith("/adult")) return "adult";
  if (routePath.startsWith("/agencies")) return "agency";
  if (routePath.startsWith("/ai-search")) return "ai_search";
  if (routePath.startsWith("/geo-aeo")) return "ai_search";
  if (routePath.startsWith("/white-label")) return "white_label";

  return getSiteSection(routePath);
};

const getPageName = (routePath: string) => {
  if (routePath === "/") return "home";

  return routePath
    .split("/")
    .filter(Boolean)
    .map((segment) => normalizeDimensionValue(segment))
    .filter(Boolean)
    .join(" / ")
    .slice(0, MARKETING_VALUE_MAX_LENGTH);
};

const getPageDimensions = () => {
  const routePath = stripLocale(window.location.pathname);
  const siteSection = getSiteSection(routePath);
  const pageType = getPageType(routePath);

  return {
    page_name: getPageName(routePath),
    page_type: pageType,
    route_path: routePath,
    site_section: siteSection,
  };
};

const getEntryDimensions = (pageDimensions: ReturnType<typeof getPageDimensions>) => {
  const fallback = {
    entry_page_name: pageDimensions.page_name,
    entry_page_type: pageDimensions.page_type,
    entry_route_path: pageDimensions.route_path,
    entry_site_section: pageDimensions.site_section,
  };

  try {
    const existing = window.sessionStorage.getItem(SESSION_ENTRY_DIMENSIONS_KEY);

    if (existing) {
      const parsed = JSON.parse(existing) as typeof fallback;

      if (parsed.entry_route_path) {
        return parsed;
      }
    }

    window.sessionStorage.setItem(SESSION_ENTRY_DIMENSIONS_KEY, JSON.stringify(fallback));
  } catch {
    // Entry dimensions are best-effort when storage is unavailable.
  }

  return fallback;
};

const getDomain = (urlLike: string) => {
  if (!urlLike) return "";

  try {
    return new URL(urlLike, window.location.origin).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return "";
  }
};

const domainMatches = (domain: string, matchers: readonly string[]) =>
  matchers.some((matcher) => domain === matcher || domain.endsWith(`.${matcher}`) || domain.includes(matcher));

const getSearchEngine = (domain: string) => {
  if (!domainMatches(domain, SEARCH_REFERRER_DOMAINS)) {
    return undefined;
  }

  if (domain.includes("google.")) return "google";
  if (domain.includes("bing.com")) return "bing";
  if (domain.includes("duckduckgo.com")) return "duckduckgo";
  if (domain.includes("yahoo.")) return "yahoo";
  if (domain.includes("yandex.")) return "yandex";
  if (domain.includes("baidu.")) return "baidu";
  if (domain.includes("ecosia.org")) return "ecosia";
  if (domain.includes("brave.com")) return "brave";

  return "search_engine";
};

const getReferrerType = (referrer: string) => {
  if (!referrer) return "direct";

  try {
    const url = new URL(referrer);
    const domain = getDomain(referrer);

    if (url.origin === window.location.origin) return "internal";
    if (url.protocol === "imap:" || url.protocol === "mail:") return "email";
    if (domainMatches(domain, AI_REFERRER_DOMAINS)) return "conversational_ai";
    if (domainMatches(domain, SEARCH_REFERRER_DOMAINS)) return "search";
    if (domainMatches(domain, SOCIAL_REFERRER_DOMAINS)) return "social";

    return "referral";
  } catch {
    return "other";
  }
};

const getOriginalReferrerDimensions = (referrer: string) => {
  const fallback = {
    original_referrer_type: getReferrerType(referrer),
    original_referring_domain: getDomain(referrer) || "direct",
  };

  try {
    const existing = window.localStorage.getItem(ORIGINAL_REFERRER_STORAGE_KEY);

    if (existing) {
      const parsed = JSON.parse(existing) as typeof fallback;

      if (parsed.original_referring_domain) {
        return parsed;
      }
    }

    window.localStorage.setItem(ORIGINAL_REFERRER_STORAGE_KEY, JSON.stringify(fallback));
  } catch {
    // Original referrer is best-effort when storage is unavailable.
  }

  return fallback;
};

const ACQUISITION_STRING_DIMENSION_KEYS = [
  "campaign_tracking",
  "marketing_channel",
  "original_referrer_type",
  "original_referring_domain",
  "referrer_domain",
  "referrer_type",
  "search_engine",
  "utm_campaign",
  "utm_content",
  "utm_medium",
  "utm_source",
  "utm_term",
] as const;
const ACQUISITION_BOOLEAN_DIMENSION_KEYS = ["gclid_present", "fbclid_present", "msclkid_present"] as const;

const readStoredAcquisitionDimensions = (value: unknown): Properties | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const dimensions: Properties = {};

  ACQUISITION_STRING_DIMENSION_KEYS.forEach((key) => {
    const rawValue = candidate[key];

    if (typeof rawValue !== "string" || PII_VALUE_PATTERN.test(rawValue) || /(?:https?:\/\/|www\.)/i.test(rawValue)) {
      return;
    }

    const normalizedValue = normalizeDimensionValue(rawValue);

    if (normalizedValue) {
      dimensions[key] = normalizedValue;
    }
  });

  ACQUISITION_BOOLEAN_DIMENSION_KEYS.forEach((key) => {
    if (candidate[key] === true) {
      dimensions[key] = true;
    }
  });

  return typeof dimensions.marketing_channel === "string" ? dimensions : null;
};

const getCurrentAcquisitionDimensions = (): Properties => {
  const referrer = document.referrer || "";
  const referrerDomain = getDomain(referrer);
  const referrerType = getReferrerType(referrer);
  const originalReferrer = getOriginalReferrerDimensions(referrer);
  const utmSource = safeSearchParam("utm_source");
  const utmMedium = safeSearchParam("utm_medium");
  const utmCampaign = safeSearchParam("utm_campaign");
  const utmContent = safeSearchParam("utm_content");
  const utmTerm = safeSearchParam("utm_term");
  const gclid = safeSearchParam("gclid");
  const fbclid = safeSearchParam("fbclid");
  const msclkid = safeSearchParam("msclkid");
  let marketingChannel = referrerType;

  if ((utmMedium && PAID_SEARCH_MEDIUMS.has(utmMedium)) || gclid || msclkid) {
    marketingChannel = "paid_search";
  } else if (fbclid) {
    marketingChannel = "paid_social";
  } else if (utmSource || utmMedium) {
    marketingChannel = "campaign";
  }

  return {
    gclid_present: Boolean(gclid) || undefined,
    fbclid_present: Boolean(fbclid) || undefined,
    msclkid_present: Boolean(msclkid) || undefined,
    campaign_tracking: utmCampaign,
    marketing_channel: marketingChannel,
    original_referrer_type: originalReferrer.original_referrer_type,
    original_referring_domain: originalReferrer.original_referring_domain,
    referrer_domain: referrerDomain || "direct",
    referrer_type: referrerType,
    search_engine: getSearchEngine(referrerDomain),
    utm_campaign: utmCampaign,
    utm_content: utmContent,
    utm_medium: utmMedium,
    utm_source: utmSource,
    utm_term: utmTerm,
  };
};

const hasExplicitCampaignAttribution = (dimensions: Properties) =>
  Boolean(
    dimensions.campaign_tracking ||
      dimensions.utm_source ||
      dimensions.utm_medium ||
      dimensions.gclid_present ||
      dimensions.fbclid_present ||
      dimensions.msclkid_present,
  );

const getAcquisitionDimensions = (): Properties => {
  const current = getCurrentAcquisitionDimensions();

  try {
    const existing = window.sessionStorage.getItem(SESSION_ACQUISITION_DIMENSIONS_KEY);

    if (existing && !hasExplicitCampaignAttribution(current)) {
      const stored = readStoredAcquisitionDimensions(JSON.parse(existing));

      if (stored) {
        return stored;
      }
    }

    window.sessionStorage.setItem(SESSION_ACQUISITION_DIMENSIONS_KEY, JSON.stringify(current));
  } catch {
    // Session acquisition is best-effort when storage is unavailable or malformed.
  }

  return current;
};

const bucketNumber = (value: number, edges: readonly number[]) => {
  if (!Number.isFinite(value) || value <= 0) {
    return "unknown";
  }

  let lower = 0;

  for (const edge of edges) {
    if (value <= edge) {
      return `b_${lower}_${edge}`;
    }

    lower = edge + 1;
  }

  return `b_${lower}_plus`;
};

const getTechnologyDimensions = () => ({
  browser_height_bucket: bucketNumber(window.innerHeight, [479, 767, 899, 1079, 1439]),
  browser_language: normalizeDimensionValue(navigator.language || document.documentElement.lang || "unknown"),
  browser_width_bucket: bucketNumber(window.innerWidth, [479, 767, 1023, 1279, 1439, 1919]),
  color_scheme: document.documentElement.getAttribute("data-bs-theme") || "unknown",
  cookie_support: navigator.cookieEnabled,
  device_pixel_ratio_bucket: bucketNumber(window.devicePixelRatio || 1, [1, 2, 3]),
  orientation: window.innerWidth >= window.innerHeight ? "landscape" : "portrait",
});

const daysSinceBucket = (days: number | null) => {
  if (days === null) return "first_visit";
  if (days <= 0) return "same_day";
  if (days === 1) return "1_day";
  if (days <= 7) return "2_7_days";
  if (days <= 30) return "8_30_days";
  if (days <= 90) return "31_90_days";

  return "90_plus_days";
};

const getTimeDimensions = () => {
  const now = new Date();
  const dayOfWeek = now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  const hour = now.getHours();

  return {
    local_am_pm: hour < 12 ? "am" : "pm",
    local_day_of_week: dayOfWeek,
    local_hour_of_day: String(hour).padStart(2, "0"),
    local_weekday_weekend: dayOfWeek === "saturday" || dayOfWeek === "sunday" ? "weekend" : "weekday",
  };
};

const getVisitProfile = () => {
  const fallback = {
    days_since_last_visit_bucket: "unknown",
    is_first_visit: false,
    return_frequency: "unknown",
    visit_number: 0,
  };

  try {
    const existing = window.sessionStorage.getItem(SESSION_VISIT_PROFILE_KEY);

    if (existing) {
      const parsed = JSON.parse(existing) as typeof fallback;

      if (typeof parsed.visit_number === "number") {
        return parsed;
      }
    }

    const now = Date.now();
    const previousVisitAt = Number(window.localStorage.getItem(LAST_VISIT_AT_STORAGE_KEY) || "0") || null;
    const currentVisitNumber = Number(window.localStorage.getItem(VISIT_NUMBER_STORAGE_KEY) || "0") || 0;
    const visitNumber = currentVisitNumber + 1;
    const daysSinceLastVisit =
      previousVisitAt && now > previousVisitAt ? Math.floor((now - previousVisitAt) / 86_400_000) : null;
    const bucket = daysSinceBucket(daysSinceLastVisit);
    const profile = {
      days_since_last_visit_bucket: bucket,
      is_first_visit: visitNumber === 1,
      return_frequency: visitNumber === 1 ? "first_visit" : bucket,
      visit_number: visitNumber,
    };

    window.localStorage.setItem(VISIT_NUMBER_STORAGE_KEY, String(visitNumber));
    window.localStorage.setItem(LAST_VISIT_AT_STORAGE_KEY, String(now));
    window.sessionStorage.setItem(SESSION_VISIT_PROFILE_KEY, JSON.stringify(profile));

    return profile;
  } catch {
    return fallback;
  }
};

const getSessionPageDepth = (increment = false) => {
  try {
    const currentDepth = Number(window.sessionStorage.getItem(SESSION_PAGE_DEPTH_KEY) || "0") || 0;
    const nextDepth = increment ? currentDepth + 1 : currentDepth || 1;

    if (increment) {
      window.sessionStorage.setItem(SESSION_PAGE_DEPTH_KEY, String(nextDepth));
    }

    return nextDepth;
  } catch {
    return 1;
  }
};

const getLocale = () => {
  const pathLocale = window.location.pathname.match(LOCALE_PATTERN)?.[1]?.toLowerCase();

  if (pathLocale && APP_LOCALES.includes(pathLocale as (typeof APP_LOCALES)[number])) {
    return pathLocale;
  }

  const htmlLocale = (document.documentElement.lang || "").toLowerCase();
  const htmlMatch = APP_LOCALES.find((locale) => htmlLocale === locale || htmlLocale.startsWith(`${locale}-`));

  return htmlMatch || "en";
};

const getSurface = () => {
  const explicitSurface = new URLSearchParams(window.location.search).get("surface");

  if (explicitSurface === "mobile_app" || explicitSurface === "pwa" || explicitSurface === "web") {
    return explicitSurface;
  }

  if (
    navigator.standalone === true ||
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches)
  ) {
    return "pwa";
  }

  return "web";
};

const getDeviceShell = () => document.body?.getAttribute("data-device-shell") || "unknown";

const getLocaleDirection = (): "ltr" | "rtl" => (document.documentElement.dir === "rtl" ? "rtl" : "ltr");

const getResolvedPersona = () => normalizeExperienceToken(document.documentElement.getAttribute("data-persona"), "none") || "none";

const getSharedProperties = (options: { readonly incrementPageDepth?: boolean } = {}): Properties => {
  const pageDimensions = getPageDimensions();

  return {
    ...withPostHogTrustedReleaseIdentity(scrubProperties({
      $current_url: redactUrl(window.location.href),
      $pathname: window.location.pathname,
      ...getAcquisitionDimensions(),
      ...getEntryDimensions(pageDimensions),
      ...POSTHOG_RELEASE_PROPERTIES,
      ...getTechnologyDimensions(),
      ...getTimeDimensions(),
      ...getVisitProfile(),
      demo_mode: safeSearchParam("demo"),
      device_shell: getDeviceShell(),
      hit_depth: getSessionPageDepth(options.incrementPageDepth),
      locale: getLocale(),
      locale_direction: getLocaleDirection(),
      qa_mode: getStoredQaMode(),
      ...pageDimensions,
      persona: getResolvedPersona(),
      search_crawler: document.documentElement.getAttribute("data-search-crawler") || "none",
      surface: getSurface(),
    }), POSTHOG_RELEASE_PROPERTIES),
    // This is a locally validated, opaque QA label. Add it after the generic
    // PII value scrubber, whose phone-like-number detector intentionally drops
    // numeric QA suffixes such as qa_consent_check_20260906.
    ...getSyntheticQaProperties(),
  };
};

const sanitizeMarketingPayload = (payload: Record<string, unknown> = {}) => {
  const safePayload: Properties = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (!MARKETING_PAYLOAD_KEYS.has(key) || value === undefined || value === null) {
      return;
    }

    if (typeof value === "number") {
      if (Number.isFinite(value)) {
        safePayload[key] = value;
      }

      return;
    }

    if (typeof value === "boolean") {
      safePayload[key] = value;
      return;
    }

    const normalizedValue = redactUrlLikeValue(key, String(value)).slice(0, MARKETING_VALUE_MAX_LENGTH);

    if (!normalizedValue || PII_VALUE_PATTERN.test(normalizedValue)) {
      return;
    }

    safePayload[key] = normalizedValue;
  });

  return safePayload;
};

const capturePageview = () => {
  if (!posthogClient || !canCapturePostHog()) {
    return false;
  }

  const pageviewKeyProperties = getSharedProperties();
  const pageviewKey = `${pageviewKeyProperties.surface}:${pageviewKeyProperties.locale}:${pageviewKeyProperties.route_path}:${pageviewKeyProperties.$current_url}`;

  if (lastPageviewKey === pageviewKey) {
    return false;
  }

  lastPageviewKey = pageviewKey;
  const sharedProperties = getSharedProperties({ incrementPageDepth: true });
  posthogClient.register?.(sharedProperties);
  recordLocalDebugEvent("$pageview", sharedProperties);
  posthogClient.capture("$pageview", sharedProperties, POSTHOG_CAPTURE_OPTIONS);
  return true;
};

const captureAppOpened = () => {
  if (!posthogClient || !canCapturePostHog() || appOpenedCaptured) {
    return false;
  }

  const surface = getSurface();

  if (surface !== "pwa" && surface !== "mobile_app") {
    return false;
  }

  const sessionKey = `seoh:app-opened:${surface}`;

  try {
    if (window.sessionStorage.getItem(sessionKey) === "true") {
      appOpenedCaptured = true;
      return false;
    }

    window.sessionStorage.setItem(sessionKey, "true");
  } catch {
    // Session storage is best-effort; still capture once for this runtime.
  }

  appOpenedCaptured = true;
  return captureMarketingEvent("app_opened", {
    app_surface: surface,
    display_mode: surface === "pwa" ? "standalone" : "mobile_app",
  });
};

const captureMarketingEventDetailed = (eventName: string, payload: Record<string, unknown> = {}) => {
  if (!canCapturePostHog() || !isPostHogEventName(eventName)) {
    return { captured: false, retained: false };
  }

  marketingDeliveryDiagnostics.lastState = "constructed";
  const safePayload = sanitizeMarketingPayload(payload);
  const properties =
    eventName === "page_not_found"
      ? getPrivacySafeNotFoundProperties(safePayload)
      : {
          ...getSharedProperties(),
          ...safePayload,
        };
  const localDebugCaptured = isLocalDebugHost();

  if (localDebugCaptured) {
    recordLocalDebugEvent(eventName, properties);
  }

  if (!posthogClient) {
    if (!isConfigured()) {
      if (localDebugCaptured) marketingDeliveryDiagnostics.lastState = "local_debug";
      return { captured: localDebugCaptured, retained: localDebugCaptured };
    }

    const queued = enqueueEvent(pendingMarketingEvents, { eventName, payload: safePayload });
    pendingMarketingEvents = queued.queue;
    marketingDeliveryDiagnostics.queued += 1;
    marketingDeliveryDiagnostics.droppedOverflow += queued.dropped.length;
    marketingDeliveryDiagnostics.lastState = "queued";
    void initializePostHog().then(flushPendingMarketingEvents);

    return { captured: false, retained: true };
  }

  try {
    posthogClient.capture(eventName, properties, POSTHOG_CAPTURE_OPTIONS);
    marketingDeliveryDiagnostics.handedToCollector += 1;
    marketingDeliveryDiagnostics.lastState = "handed_to_collector";
    return { captured: true, retained: true };
  } catch {
    // A throw does not establish whether the SDK accepted the event. Do not
    // claim success or automatically replay a potentially delivered event.
    marketingDeliveryDiagnostics.deliveryUnknown += 1;
    marketingDeliveryDiagnostics.lastState = "delivery_unknown";
    return { captured: false, retained: true };
  }
};

// True means SDK handoff (or local-only debugging), never collector acceptance.
const captureMarketingEvent = (eventName: string, payload: Record<string, unknown> = {}) =>
  captureMarketingEventDetailed(eventName, payload).captured;

const getPrivacySafeNotFoundProperties = (safePayload: Properties): Properties =>
  scrubProperties({
    ...POSTHOG_RELEASE_PROPERTIES,
    $current_url: `${window.location.origin}/404`,
    $pathname: "/404",
    $referrer: safePayload.referrer_bucket || "none",
    $referring_domain: safePayload.referrer_bucket || "none",
    device_shell: getDeviceShell(),
    locale: safePayload.locale || getLocale(),
    locale_direction: getLocaleDirection(),
    page_name: "not_found",
    page_type: "not_found",
    route_path: safePayload.route_path || "/missing",
    site_section: "error",
    surface: getSurface(),
    ...safePayload,
  });

const normalizeExperienceToken = (value?: string | null, fallback = "") => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.:/()[\]-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, MARKETING_VALUE_MAX_LENGTH);

  return normalized && COMPONENT_IMPRESSION_SAFE_TOKEN_PATTERN.test(normalized) ? normalized : fallback;
};

const getRouteTemplateId = (routePath = stripLocale(window.location.pathname)) => {
  if (routePath === "/") return "home";
  if (routePath === "/services") return "services";
  if (routePath.startsWith("/services/")) return "services/[slug]";
  if (routePath === "/resources") return "resources";
  if (routePath.startsWith("/resources/")) return "resources/[slug]";
  if (routePath === "/blog-detail" || routePath.startsWith("/blog-detail/")) return "blog-detail/[id]";
  if (routePath === "/adult" || routePath.startsWith("/adult/")) return "adult";
  if (routePath.startsWith("/client-portal")) return "client-portal";

  const firstSegment = routePath.split("/").filter(Boolean)[0] || "home";
  return normalizeExperienceToken(firstSegment, "unknown");
};

const routePathMatchesPattern = (routePath: string, pattern: string) => {
  if (!pattern || pattern === "/*") return true;
  if (pattern === routePath) return true;
  if (pattern.endsWith("/*")) {
    const prefix = pattern.slice(0, -2);
    return routePath === prefix || routePath.startsWith(`${prefix}/`);
  }
  if (pattern.endsWith("*")) {
    return routePath.startsWith(pattern.slice(0, -1));
  }
  return false;
};

const componentIdMatchesPattern = (pattern: string, componentId: string) => {
  if (!pattern || !componentId) return false;
  if (pattern === componentId) return true;
  if (!pattern.includes("*")) return componentId.startsWith(pattern);

  const escaped = pattern.split("*").map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join(".*");
  return new RegExp(`^${escaped}$`).test(componentId);
};

const parseBrowserExperienceRegistry = (value: unknown): BrowserExperienceVariantRegistry | null => {
  if (!isExperienceVariantRegistryExport(value)) {
    return null;
  }

  const treatmentFresh = isExperienceVariantRegistryFresh(value);
  const hasUnsafeVariant = value.variants.some((variant) => {
    const isTreatment = variant.variant_key !== "control";
    const isAdult = variant.vertical_18plus || variant.adult_review_required;

    if (isTreatment && (!treatmentFresh || value.source_crm_version === EXPERIENCE_VARIANT_REGISTRY_SOURCE_CRM_VERSION)) {
      return true;
    }

    if (!isAdult) {
      return false;
    }

    if (isTreatment) {
      return variant.marketing_compliance_gate !== "passed";
    }

    return !["control_static", "passed"].includes(variant.marketing_compliance_gate);
  });

  return hasUnsafeVariant ? null : value;
};

const loadBrowserExperienceRegistry = () => {
  if (browserExperienceRegistryCache !== undefined) {
    browserExperienceRegistryReady = true;
    return Promise.resolve(browserExperienceRegistryCache);
  }

  if (browserExperienceRegistryLoadPromise) {
    return browserExperienceRegistryLoadPromise;
  }

  const inlineRegistryScript = document.getElementById(EXPERIENCE_VARIANT_REGISTRY_SCRIPT_ID);
  if (inlineRegistryScript?.textContent) {
    try {
      browserExperienceRegistryCache = parseBrowserExperienceRegistry(JSON.parse(inlineRegistryScript.textContent));
      browserExperienceRegistryReady = true;
      return Promise.resolve(browserExperienceRegistryCache);
    } catch {
      // Continue to the browser-safe endpoint during a mixed-version deployment.
    }
  }

  const abortController = new AbortController();
  const timeout = window.setTimeout(() => abortController.abort(), EXPERIENCE_VARIANT_REGISTRY_TIMEOUT_MS);

  browserExperienceRegistryLoadPromise = fetch(EXPERIENCE_VARIANT_REGISTRY_URL, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    signal: abortController.signal,
  })
    .then(async (response) => (response.ok ? parseBrowserExperienceRegistry(await response.json()) : null))
    .catch(() => null)
    .then((registry) => {
      browserExperienceRegistryCache = registry;
      return registry;
    })
    .finally(() => {
      browserExperienceRegistryReady = true;
      browserExperienceRegistryLoadPromise = undefined;
      window.clearTimeout(timeout);
    });

  return browserExperienceRegistryLoadPromise;
};

const readBrowserExperienceRegistry = () => browserExperienceRegistryCache ?? null;

const findBrowserExperienceVariant = ({
  componentId,
  experienceId,
  routePath,
}: {
  readonly componentId: string;
  readonly experienceId?: string;
  readonly routePath: string;
}) => {
  const variants = readBrowserExperienceRegistry()?.variants || [];

  return variants.find((variant) => {
    if (variant.status && variant.status !== "active") {
      return false;
    }

    const variantComponentId = normalizeExperienceToken(variant.component_id);
    const variantExperienceId = normalizeExperienceToken(variant.experience_id);
    const componentMatches = variantComponentId && componentIdMatchesPattern(variantComponentId, componentId);
    const experienceMatches = experienceId && variantExperienceId === experienceId;

    if (!componentMatches && !experienceMatches) {
      return false;
    }

    const routePaths = Array.isArray(variant.route_paths) ? variant.route_paths : [];
    return routePaths.length === 0 || routePaths.some((routePattern) => routePathMatchesPattern(routePath, String(routePattern)));
  });
};

type RemoteExperienceAssignment = {
  readonly variantKey: string;
  readonly globalHoldout?: true;
  readonly holdoutKey?: typeof EXPERIENCE_GLOBAL_HOLDOUT_KEY;
};

const getRemoteExperienceAssignment = (componentId: string, routePath: string): RemoteExperienceAssignment | undefined => {
  const flagValue = getPostHogFeatureFlagValue("seoh-experience-registry");
  if (isHoldoutVariantKey(flagValue)) {
    return {
      globalHoldout: true,
      holdoutKey: EXPERIENCE_GLOBAL_HOLDOUT_KEY,
      variantKey: "control",
    };
  }

  const payload = getPostHogFeatureFlagPayload("seoh-experience-registry");

  if (!payload || payload.kind !== "experience_registry" || payload.mode !== "test") {
    return undefined;
  }

  if (payload.routePaths.length && !payload.routePaths.some((pathPattern) => routePathMatchesPattern(routePath, pathPattern))) {
    return undefined;
  }

  const match = payload.variants.find((variant) => componentIdMatchesPattern(variant.componentId, componentId));
  const variantKey = match?.variantKey ? normalizeExperienceToken(match.variantKey, "control") : undefined;

  if (!variantKey) {
    return undefined;
  }

  if (isHoldoutVariantKey(variantKey)) {
    return {
      globalHoldout: true,
      holdoutKey: EXPERIENCE_GLOBAL_HOLDOUT_KEY,
      variantKey: "control",
    };
  }

  return { variantKey };
};

const trackingElementSelector = "[data-analytics-component-id], [data-analytics-cta-id], [data-analytics-experience-id]";

const getTrackingElement = (element: Element) => {
  if (element.matches(trackingElementSelector)) {
    return element;
  }

  return element.closest(trackingElementSelector) || element.querySelector(trackingElementSelector) || element;
};

const getElementZone = (element: Element, trackingElement: Element) => {
  const zone = normalizeExperienceToken(
    element.getAttribute("data-seoh-xp-zone") || trackingElement.closest("[data-seoh-xp-zone]")?.getAttribute("data-seoh-xp-zone"),
    "",
  );

  return EXPERIENCE_ZONES.has(zone) ? zone : undefined;
};

const getElementDepth = (element: Element, trackingElement: Element) => {
  const rawDepth =
    element.getAttribute("data-seoh-xp-depth") ||
    trackingElement.closest("[data-seoh-xp-depth]")?.getAttribute("data-seoh-xp-depth") ||
    "";
  const depth = Number.parseInt(rawDepth, 10);

  return Number.isFinite(depth) ? depth : undefined;
};

const getComponentIndexForZone = (element: Element, zone?: string) => {
  const selector = zone ? `[data-seoh-xp-zone="${zone}"]` : COMPONENT_IMPRESSION_SELECTOR;
  const elements = Array.from(document.querySelectorAll(selector));
  const index = elements.indexOf(element);

  return index >= 0 ? index + 1 : 1;
};

const getSemanticComponentSlug = (element: Element) => {
  if (element.matches("header, .marketing-site-header")) return "site-header";
  if (element.matches("footer, .marketing-footer")) return "site-footer";
  if (element.matches("main, #main-content")) return "main-content";
  if (element.matches("[data-mobile-bottom-cta], .mobile-bottom-cta")) return "mobile-bottom-cta";
  if (element.id) return element.id;

  return "";
};

const deriveComponentId = (element: Element, trackingElement: Element, routeTemplateId: string, zone?: string) => {
  const explicitComponentId = normalizeExperienceToken(trackingElement.getAttribute("data-analytics-component-id"));
  if (explicitComponentId) {
    return explicitComponentId;
  }

  const ctaId = normalizeExperienceToken(trackingElement.getAttribute("data-analytics-cta-id"));
  if (ctaId) {
    return ctaId.startsWith("cta.") ? ctaId.replace(/^cta\./, "") : ctaId;
  }

  const semanticSlug = normalizeExperienceToken(getSemanticComponentSlug(element));
  if (semanticSlug) {
    return normalizeExperienceToken(`${routeTemplateId}.${semanticSlug}`, "unknown.component");
  }

  const zoneSlug = zone || "component";
  return normalizeExperienceToken(`${routeTemplateId}.${zoneSlug}.zone-${getComponentIndexForZone(element, zone)}`, "unknown.component");
};

const getExperienceMotionMode = () => {
  if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return "reduced";
  }

  const explicit =
    document.documentElement.getAttribute("data-seoh-xp-motion") ||
    document.documentElement.getAttribute("data-seoh-xp-intensity") ||
    "";
  const normalized = normalizeExperienceToken(explicit);

  if (normalized === "full" || normalized === "reduced" || normalized === "static") {
    return normalized;
  }

  if (normalized === "disabled") {
    return "static";
  }

  return "full";
};

const getShellDetectionState = (deviceShell: string) => (deviceShell && deviceShell !== "unknown" ? "detected" : "defaulted");

const buildComponentImpressionPayload = (element: Element): ComponentImpressionPayload | null => {
  const trackingElement = getTrackingElement(element);
  const routePath = stripLocale(window.location.pathname);
  const routeTemplateId = getRouteTemplateId(routePath);
  const zone = getElementZone(element, trackingElement);
  const componentId = deriveComponentId(element, trackingElement, routeTemplateId, zone);
  const experienceId = normalizeExperienceToken(trackingElement.getAttribute("data-analytics-experience-id"));

  if (!componentId) {
    return null;
  }

  const registryRow = findBrowserExperienceVariant({ componentId, experienceId, routePath });
  const previewVariantKey = getForcedExperiencePreviewVariantKey();
  const remoteAssignment = previewVariantKey ? undefined : getRemoteExperienceAssignment(componentId, routePath);
  const remoteVariantKey = previewVariantKey || remoteAssignment?.variantKey;
  const explicitVariantKey = normalizeExperienceToken(trackingElement.getAttribute("data-analytics-variant-key"));
  const variantKey = previewVariantKey || explicitVariantKey || remoteVariantKey || normalizeExperienceToken(registryRow?.variant_key, "control");
  const ctaId = normalizeExperienceToken(trackingElement.getAttribute("data-analytics-cta-id") || registryRow?.cta_id);
  const deviceShell = normalizeExperienceToken(getDeviceShell(), "unknown");
  const componentFamily =
    normalizeExperienceToken(trackingElement.getAttribute("data-analytics-component-family")) ||
    normalizeExperienceToken(registryRow?.component_family) ||
    (ctaId ? "cta" : zone || "component");
  const marketingComplianceGate = normalizeExperienceToken(registryRow?.marketing_compliance_gate);
  const vertical18Plus =
    Boolean(registryRow?.vertical_18plus || registryRow?.adult_review_required || routePath === "/adult" || routePath.startsWith("/adult/")) || undefined;

  return {
    campaign_key: normalizeExperienceToken(trackingElement.getAttribute("data-analytics-campaign-key") || registryRow?.campaign_key) || undefined,
    component_family: componentFamily,
    component_id: componentId,
    content_theme: normalizeExperienceToken(registryRow?.content_theme || document.documentElement.getAttribute("data-persona") || "") || undefined,
    cta_id: ctaId || undefined,
    device_shell: deviceShell,
    experience_id: experienceId || normalizeExperienceToken(registryRow?.experience_id) || undefined,
    experiment_key: remoteVariantKey ? "seoh-experience-registry" : undefined,
    experiment_variant: remoteVariantKey,
    global_holdout: remoteAssignment?.globalHoldout,
    holdout_key: remoteAssignment?.holdoutKey,
    locale_direction: getLocaleDirection(),
    marketing_compliance_gate: marketingComplianceGate || undefined,
    motion_mode: getExperienceMotionMode(),
    persona: getResolvedPersona(),
    route_template_id: normalizeExperienceToken(registryRow?.route_template_id || routeTemplateId, routeTemplateId),
    shell_detection_state: getShellDetectionState(deviceShell),
    source_locale: getLocale(),
    source_path: routePath,
    taxonomy_version: COMPONENT_IMPRESSION_TRACKING_TAXONOMY_VERSION,
    variant_key: variantKey,
    vertical_18plus: vertical18Plus,
    xp_depth: getElementDepth(element, trackingElement),
    xp_zone: zone || normalizeExperienceToken(registryRow?.xp_zone) || undefined,
  };
};

const eventNamesForComponentImpression = (payload: ComponentImpressionPayload) => {
  const events = ["component_impression"];

  if (payload.cta_id || payload.component_family === "cta" || payload.xp_zone === "cta") {
    events.push("cta_impression");
  }

  if (payload.experience_id) {
    events.push("experience_variant_exposed");
  }

  if (payload.xp_zone) {
    events.push("experience_zone_viewed");
  }

  if (isParallaxExperiencePayload(payload)) {
    events.push("parallax_variant_viewed");
  }

  return events;
};

const eventNamesForComponentEngagement = (payload: ComponentImpressionPayload) => {
  const events = ["component_engaged"];

  if (payload.cta_id || payload.component_family === "cta" || payload.xp_zone === "cta") {
    events.push("cta_engaged");
  }

  if (payload.xp_zone) {
    events.push("experience_zone_engaged");
  }

  if (isParallaxExperiencePayload(payload)) {
    events.push("parallax_variant_engaged");
  }

  return events;
};

const hasLoadedExperienceCss = () =>
  Array.from(document.styleSheets).some((sheet) => {
    try {
      return typeof sheet.href === "string" && sheet.href.includes("/assets/css/experience.");
    } catch {
      return false;
    }
  });

const isParallaxExperiencePayload = (payload: ComponentImpressionPayload) =>
  payload.motion_mode === "full" &&
  hasLoadedExperienceCss() &&
  (document.documentElement.getAttribute("data-seoh-xp-continuous") === "true" ||
    document.querySelector("[data-seoh-xp-surface]")?.getAttribute("data-seoh-xp-continuous") === "true") &&
  ["hero", "media", "panel", "section"].includes(String(payload.xp_zone || ""));

const componentImpressionSeenKey = (eventName: string, payload: ComponentImpressionPayload) =>
  [
    eventName,
    componentImpressionRouteKey,
    payload.component_id,
    payload.cta_id || "none",
    payload.variant_key,
    payload.motion_mode,
  ].join(":");

const componentEngagementSeenKey = (eventName: string, payload: ComponentImpressionPayload) =>
  componentImpressionSeenKey(
    eventName,
    eventName === "component_engaged" ? { ...payload, cta_id: undefined } : payload,
  );

const captureComponentImpressionPayload = (payload: ComponentImpressionPayload) => {
  let capturedAny = false;

  eventNamesForComponentImpression(payload).forEach((eventName) => {
    const seenKey = componentImpressionSeenKey(eventName, payload);
    if (componentImpressionSeenKeys.has(seenKey)) {
      return;
    }

    const result = captureMarketingEventDetailed(eventName, payload as unknown as Record<string, unknown>);
    if (result.retained) {
      componentImpressionSeenKeys.add(seenKey);
    }
    capturedAny ||= result.captured;
  });

  return capturedAny;
};

const captureComponentImpression = (element: Element) => {
  if (!canCapturePostHog()) {
    return false;
  }

  resetComponentImpressionsIfRouteChanged();

  const payload = buildComponentImpressionPayload(element);
  if (!payload) {
    return false;
  }

  return captureComponentImpressionPayload(payload);
};

const stringComponentEngagementValue = (payload: Record<string, unknown>, key: string, fallback = "") =>
  normalizeExperienceToken(typeof payload[key] === "string" ? payload[key] : "", fallback);

const buildProvidedComponentEngagementPayload = (
  rawPayload: Record<string, unknown>,
): ComponentImpressionPayload | null => {
  const componentId = stringComponentEngagementValue(rawPayload, "component_id");
  if (!componentId) {
    return null;
  }

  const routePath = stripLocale(window.location.pathname);
  const routeTemplateId = getRouteTemplateId(routePath);
  const deviceShell = normalizeExperienceToken(getDeviceShell(), "unknown");
  const vertical18Plus =
    rawPayload.vertical_18plus === true || routePath === "/adult" || routePath.startsWith("/adult/") || undefined;
  const xpDepth = rawPayload.xp_depth;

  return {
    campaign_key: stringComponentEngagementValue(rawPayload, "campaign_key") || undefined,
    component_family: stringComponentEngagementValue(rawPayload, "component_family", "gamification"),
    component_id: componentId,
    content_theme:
      stringComponentEngagementValue(rawPayload, "content_theme") ||
      normalizeExperienceToken(document.documentElement.getAttribute("data-persona")) ||
      undefined,
    cta_id: stringComponentEngagementValue(rawPayload, "cta_id") || undefined,
    device_shell: deviceShell,
    experience_id: stringComponentEngagementValue(rawPayload, "experience_id") || undefined,
    experiment_key: stringComponentEngagementValue(rawPayload, "experiment_key") || undefined,
    experiment_variant: stringComponentEngagementValue(rawPayload, "experiment_variant") || undefined,
    global_holdout: rawPayload.global_holdout === true || undefined,
    holdout_key:
      stringComponentEngagementValue(rawPayload, "holdout_key") === "seoh-experience-global-holdout"
        ? "seoh-experience-global-holdout"
        : undefined,
    locale_direction: getLocaleDirection(),
    marketing_compliance_gate: stringComponentEngagementValue(rawPayload, "marketing_compliance_gate") || undefined,
    motion_mode: getExperienceMotionMode(),
    persona: getResolvedPersona(),
    route_template_id: stringComponentEngagementValue(rawPayload, "route_template_id", routeTemplateId),
    shell_detection_state: getShellDetectionState(deviceShell),
    source_locale: getLocale(),
    source_path: routePath,
    taxonomy_version: stringComponentEngagementValue(
      rawPayload,
      "taxonomy_version",
      COMPONENT_IMPRESSION_TRACKING_TAXONOMY_VERSION,
    ),
    variant_key: stringComponentEngagementValue(rawPayload, "variant_key", "control"),
    vertical_18plus: vertical18Plus,
    xp_depth: typeof xpDepth === "number" || typeof xpDepth === "string" ? xpDepth : undefined,
    xp_zone: stringComponentEngagementValue(rawPayload, "xp_zone") || undefined,
  };
};

const captureProvidedComponentEngagement = (rawPayload: Record<string, unknown>) => {
  if (!canCapturePostHog()) {
    return false;
  }

  resetComponentImpressionsIfRouteChanged();

  const payload = buildProvidedComponentEngagementPayload(rawPayload);
  if (!payload) {
    return false;
  }

  captureComponentImpressionPayload(payload);

  let capturedAny = false;

  eventNamesForComponentEngagement(payload).forEach((eventName) => {
    const seenKey = componentEngagementSeenKey(eventName, payload);
    if (componentEngagementSeenKeys.has(seenKey)) {
      return;
    }

    const result = captureMarketingEventDetailed(eventName, {
      ...rawPayload,
      ...payload,
    });
    if (result.retained) {
      componentEngagementSeenKeys.add(seenKey);
    }
    capturedAny ||= result.captured;
  });

  return capturedAny;
};

const captureComponentEngagement = (element: Element) => {
  if (!canCapturePostHog()) {
    return false;
  }

  resetComponentImpressionsIfRouteChanged();

  const payload = buildComponentImpressionPayload(element);
  if (!payload) {
    return false;
  }

  captureComponentImpressionPayload(payload);

  let capturedAny = false;

  eventNamesForComponentEngagement(payload).forEach((eventName) => {
    const seenKey = componentEngagementSeenKey(eventName, payload);
    if (componentEngagementSeenKeys.has(seenKey)) {
      return;
    }

    const result = captureMarketingEventDetailed(eventName, payload as unknown as Record<string, unknown>);
    if (result.retained) {
      componentEngagementSeenKeys.add(seenKey);
    }
    capturedAny ||= result.captured;
  });

  return capturedAny;
};

const getComponentEngagementElement = (target: Element | null) => {
  const action = target?.closest(COMPONENT_ENGAGEMENT_ACTION_SELECTOR);
  if (!action) {
    return null;
  }

  return action.closest(COMPONENT_IMPRESSION_SELECTOR) || action.querySelector(COMPONENT_IMPRESSION_SELECTOR);
};

const handleComponentEngagementClick = (event: MouseEvent) => {
  const target = event.target instanceof Element ? event.target : null;
  const engagementElement = getComponentEngagementElement(target);

  if (engagementElement) {
    captureComponentEngagement(engagementElement);
  }
};

const handleComponentEngagementKeydown = (event: KeyboardEvent) => {
  if (event.repeat || (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar")) {
    return;
  }

  const target = event.target instanceof Element ? event.target : null;
  const action = target?.closest(COMPONENT_ENGAGEMENT_KEY_SELECTOR);
  if (!action) {
    return;
  }

  const engagementElement = action.closest(COMPONENT_IMPRESSION_SELECTOR) || action.querySelector(COMPONENT_IMPRESSION_SELECTOR);
  if (engagementElement) {
    captureComponentEngagement(engagementElement);
  }
};

const handleComponentEngagementSubmit = (event: SubmitEvent) => {
  const form = event.target instanceof Element ? event.target : null;
  const engagementElement = form?.closest(COMPONENT_IMPRESSION_SELECTOR) || form?.querySelector(COMPONENT_IMPRESSION_SELECTOR);

  if (engagementElement) {
    captureComponentEngagement(engagementElement);
  }
};

const isComponentImpressionVisibleFallback = (element: Element) => {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1;
  const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
  const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
  const visibleArea = visibleWidth * visibleHeight;
  const totalArea = Math.max(1, rect.width * rect.height);
  const viewportArea = Math.max(1, viewportWidth * viewportHeight);

  return visibleArea / Math.min(totalArea, viewportArea) >= COMPONENT_IMPRESSION_THRESHOLD;
};

const isComponentImpressionEntryVisible = (entry: IntersectionObserverEntry) => {
  if (!entry.isIntersecting) {
    return false;
  }

  const rect = entry.boundingClientRect;
  const intersectionRect = entry.intersectionRect;
  const rootBounds = entry.rootBounds;
  const visibleArea = Math.max(0, intersectionRect.width) * Math.max(0, intersectionRect.height);
  const totalArea = Math.max(1, rect.width * rect.height);
  const viewportArea = rootBounds
    ? Math.max(1, rootBounds.width * rootBounds.height)
    : Math.max(1, (window.innerWidth || document.documentElement.clientWidth || 1) * (window.innerHeight || document.documentElement.clientHeight || 1));

  return visibleArea / Math.min(totalArea, viewportArea) >= COMPONENT_IMPRESSION_THRESHOLD;
};

const clearComponentImpressionTimer = (element: Element) => {
  const timer = componentImpressionVisibleTimers.get(element);
  if (timer) {
    window.clearTimeout(timer);
  }

  componentImpressionVisibleTimers.delete(element);
};

const scheduleComponentImpressionDwell = (element: Element) => {
  if (componentImpressionVisibleTimers.get(element)) {
    return;
  }

  const timer = window.setTimeout(() => {
    componentImpressionVisibleTimers.delete(element);

    if (componentImpressionVisibleElements.has(element) && captureComponentImpression(element)) {
      componentImpressionObserver?.unobserve(element);
    }
  }, COMPONENT_IMPRESSION_DWELL_MS);

  componentImpressionVisibleTimers.set(element, timer);
};

const setComponentImpressionVisibility = (element: Element, visible: boolean) => {
  if (visible) {
    componentImpressionVisibleElements.add(element);
    scheduleComponentImpressionDwell(element);
    return;
  }

  componentImpressionVisibleElements.delete(element);
  clearComponentImpressionTimer(element);
};

const resetComponentImpressionsIfRouteChanged = () => {
  const nextRouteKey = `${getLocale()}:${stripLocale(window.location.pathname)}`;

  if (componentImpressionRouteKey === nextRouteKey) {
    return;
  }

  componentImpressionRouteKey = nextRouteKey;
  componentImpressionSeenKeys = new Set();
  componentImpressionVisibleElements = new WeakSet();
  componentImpressionVisibleTimers = new WeakMap();
  componentImpressionObservedElements = new WeakSet();
  componentImpressionObserver?.disconnect();
  componentImpressionObserver = null;
  componentEngagementSeenKeys = new Set();
};

const observeComponentImpressionElement = (element: Element) => {
  if (componentImpressionObservedElements.has(element)) {
    return;
  }

  componentImpressionObservedElements.add(element);
  componentImpressionObserver?.observe(element);
};

const syncComponentImpressionTracking = () => {
  resetComponentImpressionsIfRouteChanged();

  const elements = Array.from(document.querySelectorAll(COMPONENT_IMPRESSION_SELECTOR));
  if (elements.length === 0) {
    return;
  }

  if (!("IntersectionObserver" in window)) {
    elements.filter(isComponentImpressionVisibleFallback).forEach((element) => {
      componentImpressionVisibleElements.add(element);
      scheduleComponentImpressionDwell(element);
    });
    return;
  }

  componentImpressionObserver =
    componentImpressionObserver ||
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setComponentImpressionVisibility(entry.target, isComponentImpressionEntryVisible(entry));
        });
      },
      { threshold: [0, COMPONENT_IMPRESSION_THRESHOLD, 1] },
    );

  elements.forEach(observeComponentImpressionElement);
};

const scheduleComponentImpressionSync = () => {
  if (!isBrowser() || componentImpressionSyncFrameRequested) {
    return;
  }

  if (!browserExperienceRegistryReady) {
    void loadBrowserExperienceRegistry().then(scheduleComponentImpressionSync);
    return;
  }

  componentImpressionSyncFrameRequested = true;
  window.requestAnimationFrame(() => {
    componentImpressionSyncFrameRequested = false;
    syncComponentImpressionTracking();
  });
};

const setupComponentImpressionMutationObserver = () => {
  if (componentImpressionMutationObserver || !("MutationObserver" in window)) {
    return;
  }

  componentImpressionMutationObserver = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => node instanceof Element))) {
      scheduleComponentImpressionSync();
    }
  });
  componentImpressionMutationObserver.observe(document.documentElement, { childList: true, subtree: true });
};

const setupComponentImpressionTracking = () => {
  document.addEventListener("click", handleComponentEngagementClick, true);
  document.addEventListener("keydown", handleComponentEngagementKeydown, true);
  document.addEventListener("submit", handleComponentEngagementSubmit, true);

  void loadBrowserExperienceRegistry().finally(() => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", scheduleComponentImpressionSync, { once: true });
    }

    window.addEventListener("load", scheduleComponentImpressionSync, { once: true });
    setupComponentImpressionMutationObserver();
    scheduleComponentImpressionSync();
  });
};

const flushPendingMarketingEvents = () => {
  const drained = drainEvents(pendingMarketingEvents, canCapturePostHog(), Boolean(posthogClient));
  pendingMarketingEvents = drained.queue;
  marketingDeliveryDiagnostics.droppedOnWithdrawal += drained.dropped.length;
  if (drained.dropped.length) marketingDeliveryDiagnostics.lastState = "discarded";

  drained.events.forEach((event) => {
    captureMarketingEvent(event.eventName, event.payload);
  });
};

const identifyPortalUser = (safeUserId?: string | null) => {
  const normalizedId = String(safeUserId || "").trim();

  if (!posthogClient || !canCapturePostHog() || !isPostHogPortalDistinctId(normalizedId)) {
    return false;
  }

  posthogClient.identify?.(normalizedId, getSharedProperties());
  return true;
};

const clearPortalIdentity = () => {
  if (!posthogClient || !canCapturePostHog()) {
    return false;
  }

  posthogClient.clearIdentity?.();
  return true;
};

const disablePostHogCapture = () => {
  lastPageviewKey = "";
  marketingDeliveryDiagnostics.droppedOnWithdrawal += pendingMarketingEvents.length;
  if (pendingMarketingEvents.length) marketingDeliveryDiagnostics.lastState = "discarded";
  pendingMarketingEvents = [];
  posthogConsentTransport.withdraw(posthogClient);
  posthogClient?.stopSessionRecording?.();
  posthogClient?.opt_out_capturing?.();
};

const initializePostHog = async () => {
  if (!isBrowser() || !isConfigured() || !hasAnalyticsConsent()) {
    return null;
  }

  if (isQaAnalyticsSuppressed()) {
    disablePostHogCapture();
    return null;
  }

  posthogConsentTransport.allow();

  disablePostHogToolbarUnlessAllowed();
  const telemetryOnly = isPageNotFoundTelemetrySurface();

  if (posthogClient) {
    posthogConsentTransport.install(posthogClient);
    preparePostHogCapture(posthogClient, telemetryOnly);

    syncPostHogReplayState(posthogClient);

    if (!telemetryOnly) {
      capturePageview();
      captureAppOpened();
      scheduleComponentImpressionSync();
    }
    flushPendingMarketingEvents();
    return posthogClient;
  }

  if (posthogLoadPromise) {
    return posthogLoadPromise;
  }

  posthogLoadPromise = import("posthog-js")
    .then((module) => {
      if (!canCapturePostHog()) {
        return null;
      }

      const client = module.default as unknown as PostHogClient;

      // Install before init so feature-flag/bootstrap requests receive the
      // same consent epoch as later capture and retry requests.
      posthogConsentTransport.install(client);
      client.init(POSTHOG_TOKEN, {
        advanced_disable_feature_flags: telemetryOnly,
        api_host: POSTHOG_HOST,
        autocapture: !telemetryOnly,
        before_send: scrubCaptureResult,
        capture_dead_clicks: !telemetryOnly,
        capture_exceptions: telemetryOnly
          ? false
          : {
              capture_console_errors: true,
              capture_unhandled_errors: true,
              capture_unhandled_rejections: true,
            },
        capture_heatmaps: !telemetryOnly,
        capture_pageleave: !telemetryOnly,
        capture_pageview: false,
        capture_performance: telemetryOnly
          ? false
          : {
              network_timing: true,
              web_vitals: true,
              web_vitals_allowed_metrics: [...POSTHOG_WEB_VITALS],
              web_vitals_attribution: true,
            },
        custom_personal_data_properties: POSTHOG_PERSONAL_DATA_PROPERTIES,
        defaults: "2026-01-30",
        // Native gzip encoding can defer a collector request until after
        // analytics consent is withdrawn. Keep this transport synchronous.
        disable_compression: true,
        disable_product_tours: telemetryOnly,
        disable_session_recording: telemetryOnly || !canRecordPostHogReplay(),
        disable_surveys: telemetryOnly,
        disable_web_experiments: telemetryOnly,
        enable_recording_console_log: false,
        logs: {
          captureConsoleLogs: false,
          serviceName: "seoh-site",
        },
        mask_all_element_attributes: true,
        mask_all_text: true,
        mask_personal_data_properties: true,
        opt_out_capturing_by_default: false,
        opt_out_capturing_persistence_type: "localStorage",
        // The isolated, validated synthetic QA session is browser automation.
        // Allow only that session through PostHog's webdriver bot filter so its
        // consent and masking receipt can be verified. Ordinary visitors retain
        // the SDK default filter.
        opt_out_useragent_filter: Boolean(getSyntheticQaRunId()),
        persistence: "localStorage+cookie",
        person_profiles: "always",
        property_denylist: POSTHOG_PROPERTY_DENYLIST,
        // PostHog's public opt-out API does not discard its internal request
        // queue. Send each consented capture immediately so a queued
        // autocapture, heatmap, or Web Vitals event cannot flush after consent
        // is withdrawn.
        request_batching: false,
        respect_dnt: false,
        session_recording: {
          captureCanvas: {
            canvasFps: 12,
            canvasQuality: "1",
            recordCanvas: false,
          },
          maskAllInputs: true,
          maskTextSelector: "*",
          recordBody: false,
          recordCrossOriginIframes: false,
          recordHeaders: false,
        },
        ui_host: POSTHOG_UI_HOST,
      });

      posthogClient = client;
      preparePostHogCapture(client, telemetryOnly);

      syncPostHogReplayState(client);

      if (!telemetryOnly) {
        capturePageview();
        captureAppOpened();
        scheduleComponentImpressionSync();
      }
      flushPendingMarketingEvents();
      return client;
    })
    .catch(() => null)
    .finally(() => {
      posthogLoadPromise = null;
    });

  return posthogLoadPromise;
};

const handleConsentChange = () => {
  if (canCapturePostHog()) {
    void initializePostHog();
    if (!isPageNotFoundTelemetrySurface()) {
      scheduleComponentImpressionSync();
    }
    return;
  }

  disablePostHogCapture();
};

const schedulePageviewCapture = () => {
  if (isPageNotFoundTelemetrySurface()) {
    return;
  }

  disablePostHogToolbarUnlessAllowed();
  syncExperiencePreviewMetadata();

  if (lastRouterTransitionTimer) {
    window.clearTimeout(lastRouterTransitionTimer);
  }

  lastRouterTransitionTimer = window.setTimeout(() => {
    if (!canCapturePostHog()) {
      disablePostHogCapture();
      return;
    }

    if (posthogClient) {
      syncPostHogReplayState(posthogClient);
      capturePageview();
      scheduleComponentImpressionSync();
      return;
    }

    void initializePostHog();
    scheduleComponentImpressionSync();
  }, 120);
};

if (isBrowser() && !window.__seohPostHogInstrumentationMounted) {
  window.__seohPostHogInstrumentationMounted = true;
  window.__seohPostHogCaptureComponentEngagement = captureProvidedComponentEngagement;
  window.__seohPostHogTrackEvent = captureMarketingEvent;
  window.__seohPostHogGetDeliveryDiagnostics = getMarketingDeliveryDiagnostics;
  if (isLocalDebugHost()) {
    window.__seohPostHogGetLocalSharedProperties = () => getSharedProperties();
    window.__seohPostHogFlush = () => posthogClient?.flush?.("fetch") ?? Promise.resolve();
  }
  window.__seohPostHogCapturePageview = capturePageview;
  window.__seohPostHogCancelSurvey = cancelPostHogSurvey;
  window.__seohPostHogCanRenderSurvey = canRenderPostHogSurvey;
  window.__seohPostHogDisplaySurvey = displayPostHogSurvey;
  window.__seohPostHogFeatureFlagDefaults = POSTHOG_FEATURE_FLAG_DEFAULTS;
  window.__seohPostHogGetFeatureFlag = getPostHogFeatureFlagValue;
  window.__seohPostHogGetFeatureFlagPayload = getPostHogFeatureFlagPayload;
  window.__seohPostHogGetFeatureFlags = getPostHogFeatureFlagSnapshot;
  window.__seohPostHogGetRemoteConfigPayload = getPostHogFeatureFlagPayload;
  window.__seohPostHogGetExperiencePreviewVariant = getForcedExperiencePreviewVariantKey;
  window.__seohPostHogClearPortalIdentity = clearPortalIdentity;
  window.__seohPostHogClearSupportIdentity = clearPostHogSupportIdentity;
  window.__seohPostHogHideSupportWidget = hidePostHogSupportWidget;
  window.__seohPostHogIdentifyPortalUser = identifyPortalUser;
  window.__seohPostHogIsFeatureEnabled = isPostHogFeatureEnabled;
  window.__seohPostHogQaMode = getStoredQaMode;
  window.__seohPostHogReloadFeatureFlags = reloadPostHogFeatureFlags;
  window.__seohPostHogResyncComponentImpressions = scheduleComponentImpressionSync;
  window.__seohPostHogSetQaMode = setStoredQaMode;
  window.__seohPostHogSetSupportIdentity = setPostHogSupportIdentity;
  window.__seohPostHogShowSupportWidget = showPostHogSupportWidget;
  window.__seohPostHogSubscribeToFeatureFlags = subscribeToPostHogFeatureFlags;
  window.__seohPostHogSupportTicketId = getPostHogSupportTicketId;
  window.__seohPostHogSupportWidgetAvailable = isPostHogSupportWidgetAvailable;
  window.__seohPostHogSupportWidgetSessionId = getPostHogSupportWidgetSessionId;
  window.__seohPostHogSupportWidgetVisible = () => posthogClient?.conversations?.isVisible?.() === true;
  window.__seohPostHogSurveyProgramKeys = POSTHOG_SURVEY_PROGRAM_KEYS;
  disablePostHogToolbarUnlessAllowed();
  syncExperiencePreviewMetadata();

  const consentEventName = getConsentApi()?.eventName || "seoh:consentchange";
  window.addEventListener(consentEventName, handleConsentChange);
  if (!isPageNotFoundTelemetrySurface()) {
    window.addEventListener("seoh:navigation", schedulePageviewCapture);
    window.addEventListener("popstate", schedulePageviewCapture);
    setupComponentImpressionTracking();
  }

  if (canCapturePostHog()) {
    void initializePostHog();
  }
}

export function onRouterTransitionStart(url: string, navigationType: "push" | "replace" | "traverse") {
  if (!posthogClient || !canCapturePostHog() || isPageNotFoundTelemetrySurface()) {
    return;
  }

  posthogClient.capture(
    "route_transition_started",
    {
      ...getSharedProperties(),
      navigation_type: navigationType,
      target_url: redactUrl(url),
    },
    POSTHOG_CAPTURE_OPTIONS,
  );
}
